import { CheckOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button } from 'antd'
import { sortBy } from 'lodash'
import React from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import { ReportConfiguration, ReportConfigurationModule, ResourceType } from '@/schemas'

import { ReportConfigurationModuleContainer } from './report-configurarion-module.component'

interface ReportConfigurationProps {
  config: ReportConfiguration
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  refreshConfig: () => void
}

const reorder = (res: any[], sourceIndex: number, targetIndex: number) => {
  const [item] = res.splice(sourceIndex, 1)
  res.splice(targetIndex, 0, item)
  return res.map((r, index) => ({
    ...r,
    order: index,
  }))
}

export const ReportConfigurationContainer: React.FC<ReportConfigurationProps> = ({
  config,
  canCreate,
  canUpdate,
  canDelete,
  refreshConfig,
}) => {
  const { t } = useTranslation()
  const { type } = config
  const modules = sortBy(
    config.modules.filter((m) => !m.isDeleted),
    ['order', 'createdAt']
  )

  const { run: upsertModule } = useRequest(
    (data: ReportConfigurationModule) => ({
      url: '/api/report/upsertReportConfigModule',
      method: 'post',
      data: {
        selfId: config.selfId,
        type,
        ...data,
      },
    }),
    {
      manual: true,
      throwOnError: true,
      onSuccess: () => refreshConfig(),
    }
  )

  const { run: createModule } = useRequest(
    (data: ReportConfigurationModule) => ({
      url: '/api/report/createReportConfigModule',
      method: 'post',
      data: {
        selfId: config.selfId,
        type,
        ...data,
      },
    }),
    {
      manual: true,
      throwOnError: true,
      onSuccess: () => refreshConfig(),
    }
  )

  const { run: upsertModules } = useRequest(
    (data: ReportConfigurationModule[]) => ({
      url: '/api/report/upsertReportConfigModules',
      method: 'post',
      data: {
        id: config.id,
        selfId: config.selfId,
        type: config.type,
        effectiveDate: config.effectiveDate,
        modules: data,
      },
    }),
    {
      manual: true,
      throwOnError: true,
      onSuccess: () => refreshConfig(),
    }
  )

  const toggleModule = (id: string) => {
    const module = modules.find((m) => m.id === id)
    if (module) {
      const { isChecked } = module
      upsertModule({
        ...module,
        isChecked: !isChecked,
      })
    }
  }

  const clickCreateModule = (configurationId?: number) => {
    const DEFAULT_REPORT_SET_FIELDS = ['目标', '项目/组合', '团队', '客户项目', '产品项目', '研发项目']
    createModule({
      configurationId,
      id: v4(),
      name: '自定义集合',
      order: null,
      isDefault: false,
      isExtra: false,
      isChecked: true,
      isDeleted: false,
      isImmediately: false,
      fields: DEFAULT_REPORT_SET_FIELDS.map((field) => ({
        id: v4(),
        name: field,
        order: null,
        isDefault: true,
        isChecked: true,
        isDeleted: false,
      })),
    })
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) return
    let sendOrder = []
    sendOrder = reorder(modules, result.source.index, result.destination.index)
    upsertModules(sendOrder)
  }

  return (
    <div className='flex'>
      {type !== ResourceType.PROJECT && (
        <div className='flex flex-col flex-shrink-0 w-200px items-center  fixed'>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable isDropDisabled={!canUpdate} droppableId={'configModules'} type={'configModules'}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className='space-y-8px'>
                  {modules.map((m, index) => (
                    <Draggable key={m.id} draggableId={m.id} isDragDisabled={!canUpdate} index={index}>
                      {(provided) => (
                        <div
                          className={`w-150px px-10px border-1 border-solid rounded-8px h-40px flex items-center justify-between cursor-pointer whitespace-nowrap overflow-hidden overflow-ellipsis ${
                            m.isChecked ? 'text-blue-500 border-blue-500' : 'border-light-500'
                          } ${!canUpdate ? 'cursor-not-allowed' : ''} text-gray-500 hover:text-dark-200`}
                          onClick={() => canUpdate && toggleModule(m.id)}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          <HolderOutlined className='mr-5px' {...provided.dragHandleProps} />
                          <div className='flex w-full justify-between items-center'>
                            {t(m.name)}
                            {m.isChecked && <CheckOutlined className='mr-5px' />}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <Button
            disabled={!canCreate}
            type='text'
            className={`cursor-pointer text-center pl-10px text-blue-500 w-150px !my-15px`}
            onClick={() => clickCreateModule(config.id)}
          >
            <PlusOutlined className='mr-5px' />
            {t('自定义集合')}
          </Button>
        </div>
      )}
      <div className='space-y-16px ml-200px w-full'>
        {modules
          .filter((m) => m.isChecked)
          .map((m) => (
            <ReportConfigurationModuleContainer
              key={m.id}
              selfId={config.selfId}
              module={m}
              type={type}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
              refreshConfig={refreshConfig}
            />
          ))}
      </div>
    </div>
  )
}
